import React, { useEffect, useState } from 'react';
import ReactDOM from 'react-dom';
import PhoneInput from 'react-phone-number-input';
import { isValidPhoneNumber } from 'libphonenumber-js';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';
import 'react-phone-number-input/style.css';

const PAYMENT_PHONE_STORAGE_KEY = 'trakna.chariowPaymentPhoneNumber';

const getStoredPaymentPhoneNumber = () => {
  try {
    return localStorage.getItem(PAYMENT_PHONE_STORAGE_KEY) || '';
  } catch (error) {
    return '';
  }
};

const storePaymentPhoneNumber = (phoneNumber) => {
  try {
    localStorage.setItem(PAYMENT_PHONE_STORAGE_KEY, phoneNumber);
  } catch (error) {
    // localStorage can be unavailable in private browsing modes.
  }
};

const Overlay = styled.div`
  position: fixed;
  inset: 0;
  z-index: 10000;
  display: flex;
  align-items: flex-end;
  justify-content: center;
  background: rgba(0, 0, 0, 0.55);
  padding: 16px;

  @media (min-width: 576px) {
    align-items: center;
  }
`;

const Modal = styled.div`
  width: 100%;
  max-width: 430px;
  background: #fff;
  border-radius: 16px;
  padding: 22px;
  box-shadow: 0 18px 50px rgba(0, 0, 0, 0.2);
`;

const Header = styled.div`
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 16px;
  margin-bottom: 18px;

  h3 {
    margin: 0 0 6px;
    font-size: 20px;
    line-height: 1.25;
    font-weight: 700;
    color: #161616;
  }

  p {
    margin: 0;
    font-size: 14px;
    line-height: 1.45;
    color: #666;
  }
`;

const CloseButton = styled.button`
  width: 36px;
  height: 36px;
  border: 0;
  border-radius: 50%;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  background: #f4f4f4;
  color: #111;
  font-size: 20px;
  line-height: 1;
`;

const PhoneField = styled.div`
  margin-bottom: 10px;

  .PhoneInput {
    min-height: 52px;
    padding: 0 14px;
    border: 1px solid #d8d8d8;
    border-radius: 12px;
    background: #fff;
  }

  .PhoneInputCountry {
    margin-right: 10px;
  }

  .PhoneInputInput {
    min-width: 0;
    border: 0;
    outline: 0;
    font-size: 16px;
  }
`;

const ErrorText = styled.div`
  min-height: 20px;
  margin-bottom: 12px;
  color: #d92d20;
  font-size: 13px;
`;

const Actions = styled.div`
  display: grid;
  grid-template-columns: 1fr 1.4fr;
  gap: 10px;
`;

const Button = styled.button`
  min-height: 48px;
  border: 0;
  border-radius: 12px;
  font-weight: 700;
  font-size: 15px;
  background: ${props => props.$primary ? 'linear-gradient(94deg, #E93E84 14.36%, #F46D8F 42.6%, #FE854E 80.39%)' : '#f2f2f2'};
  color: ${props => props.$primary ? '#fff' : '#1d1d1d'};
  opacity: ${props => props.disabled ? 0.7 : 1};
`;

const ChariowPhoneModal = ({ show, defaultPhoneNumber, loading, onCancel, onConfirm }) => {
  const { t } = useTranslation();
  const [phoneNumber, setPhoneNumber] = useState(defaultPhoneNumber || '');
  const [error, setError] = useState('');

  useEffect(() => {
    if (show) {
      setPhoneNumber(defaultPhoneNumber || getStoredPaymentPhoneNumber());
      setError('');
    }
  }, [show, defaultPhoneNumber]);

  if (!show) return null;

  const handleConfirm = () => {
    if (!phoneNumber || !isValidPhoneNumber(phoneNumber)) {
      setError(t('paymentPhone.invalid', { defaultValue: "Numéro de téléphone invalide. Vérifiez le numéro et l'indicatif pays." }));
      return;
    }

    storePaymentPhoneNumber(phoneNumber);
    onConfirm(phoneNumber);
  };

  const content = (
    <Overlay role="presentation" onClick={onCancel}>
      <Modal role="dialog" aria-modal="true" aria-labelledby="chariow-phone-title" onClick={(event) => event.stopPropagation()}>
        <Header>
          <div>
            <h3 id="chariow-phone-title">{t('paymentPhone.title', { defaultValue: 'Numéro de paiement' })}</h3>
            <p>{t('paymentPhone.description', { defaultValue: 'Ce numéro sera utilisé pour ouvrir la page de paiement.' })}</p>
          </div>
          <CloseButton type="button" onClick={onCancel} aria-label={t('common.close', { defaultValue: 'Fermer' })}>
            x
          </CloseButton>
        </Header>

        <PhoneField>
          <PhoneInput
            international
            countryCallingCodeEditable={false}
            defaultCountry="CI"
            value={phoneNumber}
            onChange={(value) => {
              setPhoneNumber(value || '');
              setError('');
            }}
            labels={t('login.phoneLabels', { returnObjects: true })}
          />
        </PhoneField>
        <ErrorText>{error}</ErrorText>

        <Actions>
          <Button type="button" onClick={onCancel} disabled={loading}>
            {t('paymentPhone.cancel', { defaultValue: 'Annuler' })}
          </Button>
          <Button type="button" $primary onClick={handleConfirm} disabled={loading || !phoneNumber}>
            {loading
              ? t('paymentPhone.processing', { defaultValue: 'Ouverture...' })
              : t('paymentPhone.continue', { defaultValue: 'Continuer' })}
          </Button>
        </Actions>
      </Modal>
    </Overlay>
  );

  return ReactDOM.createPortal(content, document.body);
};

export default ChariowPhoneModal;
